import { LoginSocialFacebook } from 'reactjs-social-login';
import PropTypes from 'prop-types';
import { IconButton, useToast } from '@chakra-ui/react';
import { mdiFacebook } from '@mdi/js';
import Icon from '@mdi/react';
import { GoogleLogin } from '@react-oauth/google';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  TokenWithProviderService,
  UserRegisterService,
  UserService,
} from '../../services/auth/AuthService';
import useAuthContext from '../../hooks/useAuthContext';
import { useNavigate } from 'react-router-dom';
import { HOME } from '../../routes/app/paths';
import { classNames } from '../../utils/common';
import Spin from './loaders/Spin';

export const FacebookLoginButton = ({ isRegister, isDisabled, onProcessing }) => {
  const toast = useToast();
  const { login } = useAuthContext();

  /* RQ */
  const queryClient = useQueryClient();

  /* Fn */
  const handleShowError = (error) => {
    if (error) console.log(error);

    toast({
      title: 'Error',
      description: 'Por favor, inténtelo de nuevo mas tarde',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'right-top',
    });

    if (onProcessing) onProcessing(false);
  };

  const loginFn = async (userInfo) => {
    const loginInfo = {
      grant_type: 'facebook',
      email: userInfo.email,
    };

    try {
      const fetchToken = await TokenWithProviderService(loginInfo);
      if (fetchToken?.status === 200) {
        const token = fetchToken.data.access_token;
        const fetchUser = await queryClient.fetchQuery([token], UserService);
        if (fetchUser?.status === 200) {
          login(fetchUser.user);
        }
      }
    } catch (e) {
      handleShowError(e);
      throw e;
    }
  };

  const handleSuccess = async (userInfoResponse) => {
    const userInfo = {
      name: userInfoResponse.first_name,
      last_name: userInfoResponse.last_name,
      secound_last_name: '',
      email: userInfoResponse.email,
      verified_email: true,
      phone: '',
      ads: false,
      provider: 'facebook',
      provider_id: userInfoResponse.id,
      avatar: userInfoResponse.picture?.data?.url,
    };

    /* Register */
    try {
      if (isRegister) {
        const fetchRegister = await queryClient.fetchQuery(
          [userInfo],
          UserRegisterService,
        );
        if (fetchRegister.status === 200) {
          // loguear al usuario
          await loginFn(userInfo);

          toast({
            title: '¡Registro exitoso!',
            description: 'Su cuenta ha sido creada',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'right-top',
          });
          return;
        }
        // error
        handleShowError();
      } else {
        loginFn(userInfo)
      }
    } catch (e) {
      if (e.status === 433) {
        toast({
          title: 'Error al acceder',
          description: 'El correo de su cuenta de Facebook ya existe. Por favor, acceda a través de su correo y contraseña.',
          status: 'error',
          duration: 7000,
          isClosable: true,
          position: 'right-top',
        });
      } else {
        handleShowError(e);
      }
      // } finally {
      // if (onProcessing) onProcessing(false);
    }
  };

  return (
    <LoginSocialFacebook
      appId={import.meta.env.VITE_FACEBOOK_APP_ID}
      onLoginStart={() => {
        if (onProcessing) onProcessing(true);
      }}
      onResolve={(res) => {
        handleSuccess(res.data)
      }}
      onReject={(err) => {
        handleShowError(err)
        if (onProcessing) onProcessing(false);
      }}
      className={classNames(
        isDisabled ? 'cursor-not-allowed' : 'hover:shadow-md cursor-pointer', 'rounded-full'
      )}
    >
      <IconButton
        icon={
          <Icon
            path={mdiFacebook}
            size={2}
            className="text-blue-600 rounded-full hover:shadow-md cursor-pointer"
          />
        }
        className={classNames(
          'rounded-full p-1 bg-white disabled:opacity-50',
          isDisabled ? 'cursor-not-allowed' : 'hover:shadow-md cursor-pointer',
        )}
        rounded="full"
        isDisabled={isDisabled}
      />
    </LoginSocialFacebook>
  );
};

FacebookLoginButton.propTypes = {
  isRegister: PropTypes.bool, //TODO: quitar isRegister, y aplicar el registro al loguear si no esta la cuenta
  isDisabled: PropTypes.bool,
  onProcessing: PropTypes.func,
};

export const GoogleLoginButton = ({
  isDisabled,
  onProcessing,
}) => {
  const toast = useToast();
  const { login } = useAuthContext();

  /* RQ */
  const queryClient = useQueryClient();

  /* Fn */
  const handleShowError = (error) => {
    if (error) console.log(error);

    toast({
      title: 'Error',
      description: 'Por favor, inténtelo de nuevo mas tarde',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'right-top',
    });

    if (onProcessing) onProcessing(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {

    if (onProcessing) onProcessing(true);

    try {
      const idToken = credentialResponse.credential;

      if (!idToken) {
        throw new Error('No credential received from Google');
      }

      // Enviar id_token directamente al backend
      const loginInfo = {
        grant_type: 'google',
        id_token: idToken,
      };

      const fetchToken = await TokenWithProviderService(loginInfo);

      if (fetchToken?.status === 200) {
        const token = fetchToken.data.access_token;
        const fetchUser = await queryClient.fetchQuery([token], UserService);
        if (fetchUser?.status === 200) {
          login(fetchUser.user);
          if (onProcessing) onProcessing(false);
        }
      } else {
        handleShowError();
      }
    } catch (e) {
      if (e.status === 433) {
        toast({
          title: 'Error al acceder',
          description: 'El correo de su cuenta de Google ya existe. Por favor, acceda a través de su correo y contraseña.',
          status: 'error',
          duration: 7000,
          isClosable: true,
          position: 'right-top',
        });
      } else {
        handleShowError(e);
      }
    }
  };

  const handleGoogleError = () => {
    handleShowError();
  };

  return (
    <div className="rounded-full flex justify-center items-center mx-auto">
      <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        size="large"
        shape="circle"
        logo_alignment="center"
        disabled={isDisabled}
      />
    </div>
  );
};

GoogleLoginButton.propTypes = {
  isDisabled: PropTypes.bool,
  onProcessing: PropTypes.func,
};

const SocialLogin = ({ onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const toast = useToast();

  /* Effects */
  useEffect(() => {
    if (isAuthenticated) {
      navigate(HOME);
      toast({
        title: '¡Bienvenido!',
        description: 'Ha iniciado sesión correctamente',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'right-top',
      })
      if (onClose) onClose(false);
    }
  }, [isAuthenticated]);

  return (
    <div>
      <div className="flex justify-center items-center">
        <p
          className={classNames(
            'text-center text-md font-semibold px-5',
            isProcessing && 'text-gray-500 cursor-not-allowed',
          )}
        >
          {!isProcessing && 'Entrar con mi cuenta de Google'}
          {isProcessing === 'google' && 'Iniciando sesión con Google...'}
          {isProcessing === 'facebook' && 'Iniciando sesión con Facebook...'}
        </p>

        {isProcessing && <Spin className="ml-2" size={6} color={'gray'} />}
      </div>

      <div
        className={classNames(
          'flex justify-center items-center space-x-4 mt-4',
        )}
      >
        <GoogleLoginButton
          isDisabled={isProcessing}
          onProcessing={(bool) => setIsProcessing(bool && 'google')}
        />
        {/* <FacebookLoginButton
          isDisabled={isProcessing} 
          onProcessing={(bool) => setIsProcessing(bool && 'facebook')}
        /> */}
      </div>
    </div>
  );
};

SocialLogin.propTypes = {
  onClose: PropTypes.func,
};

export default SocialLogin;
